import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Trophy, BarChart3, Shield, Activity, TrendingUp, UserCheck, AlertTriangle, RefreshCw, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { runProfileUpdate } from '../../utils/updateExistingProfiles';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [isUpdatingProfiles, setIsUpdatingProfiles] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);

  const stats = [
    { label: 'Total Users', value: '2,847', icon: Users, color: 'text-blue-600', change: '+12%' },
    { label: 'Active Companies', value: '156', icon: Shield, color: 'text-green-600', change: '+8%' },
    { label: 'Tests Completed', value: '8,924', icon: Activity, color: 'text-red-600', change: '+24%' },
    { label: 'Badges Issued', value: '1,234', icon: Trophy, color: 'text-purple-600', change: '+15%' }
  ];

  const recentActivity = [
    {
      id: 1,
      type: 'user_registered',
      details: 'New HR user from TechCorp Inc.',
      time: '5 minutes ago',
      status: 'success'
    },
    {
      id: 2,
      type: 'badge_issued',
      details: 'Gold badge issued to John Doe',
      time: '1 hour ago',
      status: 'success'
    },
    {
      id: 3,
      type: 'system_alert',
      details: 'High server load detected',
      time: '2 hours ago',
      status: 'warning'
    },
    {
      id: 4,
      type: 'company_verified',
      details: 'StartupXYZ verification completed',
      time: '4 hours ago',
      status: 'success'
    }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_registered':
        return <UserCheck className="w-5 h-5 text-green-600" />;
      case 'badge_issued':
        return <Trophy className="w-5 h-5 text-purple-600" />;
      case 'system_alert':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'company_verified':
        return <Shield className="w-5 h-5 text-blue-600" />;
      default:
        return <Activity className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <span className="px-3 py-1.5 bg-green-100 text-green-800 text-xs rounded-full shadow-sm">Success</span>;
      case 'warning':
        return <span className="px-3 py-1.5 bg-yellow-100 text-yellow-800 text-xs rounded-full shadow-sm">Warning</span>;
      case 'error':
        return <span className="px-3 py-1.5 bg-red-100 text-red-800 text-xs rounded-full shadow-sm">Error</span>;
      default:
        return <span className="px-3 py-1.5 bg-gray-100 text-gray-800 text-xs rounded-full shadow-sm">Info</span>;
    }
  };

  const handleUpdateProfiles = async () => {
    setIsUpdatingProfiles(true);
    setUpdateResult(null);
    
    try {
      const result = await runProfileUpdate();
      setUpdateResult(result);
    } catch (error) {
      console.error('Failed to update profiles:', error);
      alert('Failed to update profiles. Please check the console for details.');
    } finally {
      setIsUpdatingProfiles(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-red-800 to-red-600 text-white rounded-3xl p-6 shadow-[0_4px_20px_rgba(185,28,28,0.3)]">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-red-100 mt-2">Welcome back, {user?.name}! Monitor and manage the platform</p>
            <div className="mt-4 flex items-center space-x-2">
              <Shield className="w-5 h-5 text-red-200" />
              <span className="text-red-100 font-medium">System Administrator</span>
            </div>
          </div>
          <div className="hidden md:block">
            <img 
              src="https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=300" 
              alt="Admin Dashboard"
              className="w-32 h-32 rounded-lg object-cover opacity-80"
            />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-6">
        <Link 
          to="/admin/users"
          className="bubbly-card p-6"
        >
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center shadow-sm">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Manage Users</h3>
              <p className="text-sm text-gray-600">View and manage all users</p>
            </div>
          </div>
        </Link>

        <Link 
          to="/admin/badges"
          className="bubbly-card p-6"
        >
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center shadow-sm">
              <Trophy className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Badge Management</h3>
              <p className="text-sm text-gray-600">Issue and manage badges</p>
            </div>
          </div>
        </Link>

        <Link 
          to="/admin/analytics"
          className="bubbly-card p-6"
        >
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center shadow-sm">
              <BarChart3 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Analytics</h3>
              <p className="text-sm text-gray-600">Platform insights</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Stats Overview */}
      <div className="grid md:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bubbly-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  <div className="flex items-center mt-2">
                    <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
                    <span className="text-sm text-green-600 font-medium">{stat.change}</span>
                  </div>
                </div>
                <Icon className={`w-8 h-8 ${stat.color}`} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Activity & System Health */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bubbly-card">
          <div className="p-6 border-b border-gray-200/50">
            <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                  <div className="flex items-center space-x-4">
                    {getActivityIcon(activity.type)}
                    <div>
                      <p className="font-medium text-gray-900">{activity.details}</p>
                      <p className="text-sm text-gray-600">{activity.time}</p>
                    </div>
                  </div>
                  {getStatusBadge(activity.status)}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bubbly-card">
          <div className="p-6 border-b border-gray-200/50">
            <h2 className="text-xl font-semibold text-gray-900">System Health</h2>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Server Performance</span>
                <span className="text-green-600 font-medium">98.5%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-600 h-2.5 rounded-full shadow-sm" style={{ width: '98.5%' }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Database Health</span>
                <span className="text-green-600 font-medium">99.2%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-600 h-2.5 rounded-full shadow-sm" style={{ width: '99.2%' }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">API Response Time</span>
                <span className="text-yellow-600 font-medium">245ms</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-yellow-600 h-2.5 rounded-full shadow-sm" style={{ width: '75%' }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Storage Usage</span>
                <span className="text-blue-600 font-medium">67%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2.5 rounded-full shadow-sm" style={{ width: '67%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Utilities */}
      <div className="bubbly-card">
        <div className="p-6 border-b border-gray-200/50">
          <h2 className="text-xl font-semibold text-gray-900">Admin Utilities</h2>
          <p className="text-gray-600 text-sm mt-1">Tools for managing platform data and user verification</p>
        </div>
        <div className="p-6">
            <div className="bg-blue-50 border border-blue-200/60 rounded-2xl p-4 mb-4 shadow-sm">
            <h3 className="font-semibold text-blue-900 mb-2">Skills Verification Update</h3>
            <p className="text-blue-800 text-sm mb-4">
              Update existing jobseeker profiles to show verification status based on completed tests with scores ≥60%.
              This will add green checkmarks to profiles of users who have already passed skills validation tests.
            </p>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={handleUpdateProfiles}
                disabled={isUpdatingProfiles}
                className="bubbly-button flex items-center px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isUpdatingProfiles ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Updating Profiles...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Update Verification Status
                  </>
                )}
              </button>
              
              {updateResult && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-medium">
                    Updated {updateResult.updated} of {updateResult.total} profiles
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Platform Overview */}
      <div className="bubbly-card">
        <div className="p-6 border-b border-gray-200/50">
          <h2 className="text-xl font-semibold text-gray-900">Platform Overview</h2>
        </div>
        <div className="p-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">2,847</div>
              <div className="text-gray-600">Total Users</div>
              <div className="text-sm text-gray-500 mt-1">
                Job Seekers: 2,234 • HR: 456 • Admins: 12
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">8,924</div>
              <div className="text-gray-600">Tests Completed</div>
              <div className="text-sm text-gray-500 mt-1">
                This month: 1,456 • Success rate: 78%
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">1,234</div>
              <div className="text-gray-600">Badges Issued</div>
              <div className="text-sm text-gray-500 mt-1">
                Silver: 892 • Gold: 342
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;